import React, { useState, useEffect, useRef, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useScrollReveal from "../hooks/useScrollReveal";

const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    location: "New Delhi",
    rating: 5,
    text: "MunchhoNN's Chatpata Mix is absolutely addictive! Perfect snack for evening tea time. My entire family can't stop munching!",
    avatar: "PS",
    color: "from-orange-400 to-red-500",
  },
  {
    name: "Rahul Verma",
    location: "Mumbai",
    rating: 5,
    text: "Best Aloo Bhujia I've ever had. Reminds me of authentic homemade snacks. The quality is consistently great every single time!",
    avatar: "RV",
    color: "from-blue-400 to-indigo-500",
  },
  {
    name: "Anjali Singh",
    location: "Lucknow",
    rating: 5,
    text: "The Potato Chips Tomato Twist flavor is incredible. Super crispy and not too greasy. My kids absolutely love them!",
    avatar: "AS",
    color: "from-green-400 to-emerald-500",
  },
  {
    name: "Vikram Patel",
    location: "Ahmedabad",
    rating: 5,
    text: "Great quality snacks at very affordable prices. The packaging is always fresh and hygienic. Truly India's favourite snack brand!",
    avatar: "VP",
    color: "from-purple-400 to-pink-500",
  },
  {
    name: "Neha Gupta",
    location: "Jaipur",
    rating: 5,
    text: "Salted Pipe is my go-to evening snack. Light, perfectly crunchy, and just the right amount of salt. So addictive!",
    avatar: "NG",
    color: "from-yellow-400 to-orange-500",
  },
  {
    name: "Arjun Kumar",
    location: "Chandigarh",
    rating: 5,
    text: "MunchhoNN truly lives up to 'Mood Banaye Mast!' Great variety, consistent quality, and always available in my local store.",
    avatar: "AK",
    color: "from-teal-400 to-cyan-500",
  },
];

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [ref, isVisible] = useScrollReveal(0.1);
  const timerRef = useRef(null);

  const next = useCallback(() => setCurrent((c) => (c + 1) % TESTIMONIALS.length), []);
  const prev = useCallback(
    () => setCurrent((c) => (c - 1 + TESTIMONIALS.length) % TESTIMONIALS.length),
    []
  );

  const resetTimer = (fn) => {
    clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(next, 4500);
  };

  useEffect(() => {
    timerRef.current = setInterval(next, 4500);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const visible = [0, 1, 2].map(
    (offset) => TESTIMONIALS[(current + offset) % TESTIMONIALS.length]
  );

  return (
    <section
      id="testimonials"
      className="py-24 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50"
      data-testid="testimonials-section"
    >
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-orange-500 uppercase tracking-widest">
            Customer Love
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 mt-2">
            What Our Fans{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
              Say
            </span>
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible.map((t, idx) => (
            <motion.div
              key={`${t.name}-${current}-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className={`bg-white rounded-3xl p-8 border transition-all duration-300 ${
                idx === 1
                  ? "shadow-2xl border-orange-200 scale-105"
                  : "shadow-sm border-orange-100 opacity-80"
              }`}
              data-testid={`testimonial-card-${idx}`}
            >
              <Quote size={32} className="text-orange-200 mb-4" />
              <p className="text-stone-600 leading-relaxed mb-6 text-sm sm:text-base">
                "{t.text}"
              </p>
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center flex-shrink-0 shadow-md`}
                >
                  <span className="text-white font-bold text-sm">{t.avatar}</span>
                </div>
                <div className="flex-1">
                  <p className="font-bold text-stone-900 text-sm">{t.name}</p>
                  <p className="text-xs text-stone-400">{t.location}</p>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} size={13} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            onClick={() => resetTimer(prev)}
            className="w-12 h-12 rounded-full bg-white border border-orange-200 flex items-center justify-center hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 shadow-sm"
            data-testid="testimonial-prev-btn"
          >
            <ChevronLeft size={20} className="text-orange-500" />
          </button>

          <div className="flex gap-2">
            {TESTIMONIALS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => resetTimer(() => setCurrent(idx))}
                className={`rounded-full transition-all duration-300 ${
                  idx === current ? "w-8 h-3 bg-orange-500" : "w-3 h-3 bg-orange-200"
                }`}
                data-testid={`testimonial-dot-${idx}`}
              />
            ))}
          </div>

          <button
            onClick={() => resetTimer(next)}
            className="w-12 h-12 rounded-full bg-white border border-orange-200 flex items-center justify-center hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 shadow-sm"
            data-testid="testimonial-next-btn"
          >
            <ChevronRight size={20} className="text-orange-500" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
